import { Message } from '@arco-design/web-vue'
import { reactive, readonly, watch } from 'vue'
import { fetchStudentCheckinNotify, type CheckInNotifyContent, type StudentCheckinNotifyTask } from '@/api/modules/signIn'
import type { GlobalWsMessage } from '@/api/modules/globalWebsocket'
import { confirmModal } from '@/components/ConfirmModal/index.vue'
import i18n from '@/locales'
import router from '@/router'

const CHECKIN_ABNORMAL_REMINDER_STORAGE_KEY = 'checkin_abnormal_reminder_shown_at'
const CHECKIN_ABNORMAL_REMINDER_INTERVAL = 24 * 60 * 60 * 1000
const CHECKIN_ABNORMAL_REMINDER_DELAY = 500
const CHECKIN_POPUP_BLOCKED_ROUTE_NAME_LIST = ['signInTask', 'signInHistoryDetail']
const { t } = i18n.global

let abnormalReminderTimer: number | null = null

const signInNotifyState = reactive({
  visible: false,
  activeTaskList: [] as StudentCheckinNotifyTask[],
  abnormalTaskList: [] as StudentCheckinNotifyTask[],
  currentTaskIndex: 0
})

function getSignInNotifyTaskList() {
  return signInNotifyState.activeTaskList
}

function clearAbnormalReminderTimer() {
  if (abnormalReminderTimer !== null) {
    window.clearTimeout(abnormalReminderTimer)
    abnormalReminderTimer = null
  }
}

function isBlockedSignInPopupRoute() {
  const routeName = String(router.currentRoute.value.name || '')
  return CHECKIN_POPUP_BLOCKED_ROUTE_NAME_LIST.includes(routeName)
}

function closeAbnormalSignInReminderModal() {
  clearAbnormalReminderTimer()
  confirmModal.close()
}

function checkOutDate() {
  const lastShownTime = localStorage.getItem(CHECKIN_ABNORMAL_REMINDER_STORAGE_KEY)
  if (!lastShownTime) {
    return true
  }
  const timestamp = Number(lastShownTime)
  if (Number.isNaN(timestamp)) {
    return true
  }
  return Date.now() - timestamp >= CHECKIN_ABNORMAL_REMINDER_INTERVAL
}

function markAbnormalReminderShown() {
  localStorage.setItem(CHECKIN_ABNORMAL_REMINDER_STORAGE_KEY, Date.now().toString())
}

function openAbnormalSignInReminderModal() {
  if (isBlockedSignInPopupRoute()) {
    return
  }
  if (!checkOutDate()) {
    return
  }
  markAbnormalReminderShown()
  clearAbnormalReminderTimer()
  abnormalReminderTimer = window.setTimeout(() => {
    confirmModal.warning({
      title: t('task.signIn.abnormalReminderTitle'),
      content: t('task.signIn.abnormalWarning'),
      showCancel: false,
      okText: t('task.signIn.viewDetails'),
      onOk: async() => {
        confirmModal.close()
        await router.push({
          path: '/course',
          query: {
            showCurrent: 'true'
          }
        })
      }
    })
  }, CHECKIN_ABNORMAL_REMINDER_DELAY)
}

function normalizeActiveTaskList(taskList?: StudentCheckinNotifyTask[]) {
  return (taskList || []).filter(item => item.hasPermission !== false)
}

function openSignInNotifyModal(content: CheckInNotifyContent) {
  signInNotifyState.activeTaskList = content.activeTaskList || []
  signInNotifyState.abnormalTaskList = content.abnormalTaskList || []
  signInNotifyState.currentTaskIndex = 0
  signInNotifyState.visible = !isBlockedSignInPopupRoute() && getSignInNotifyTaskList().length > 0
}

function closeSignInNotifyModal() {
  signInNotifyState.visible = false
}

function goToNextSignInNotifyTask() {
  const taskList = getSignInNotifyTaskList()
  if (signInNotifyState.currentTaskIndex < taskList.length - 1) {
    signInNotifyState.currentTaskIndex += 1
    return
  }
  closeSignInNotifyModal()
}

function goToPrevSignInNotifyTask() {
  if (signInNotifyState.currentTaskIndex > 0) {
    signInNotifyState.currentTaskIndex -= 1
  }
}

function setCurrentSignInNotifyTask(index: number) {
  const taskList = getSignInNotifyTaskList()
  if (index < 0 || index >= taskList.length) {
    return
  }
  signInNotifyState.currentTaskIndex = index
}

function buildTaskIdentity(task: StudentCheckinNotifyTask) {
  return [
    task.attendanceId || '',
    task.taskId || '',
    task.roundId || '',
    task.classId || '',
    task.courseId || ''
  ].join('_')
}

function openSignInNotifyModalByTask(task?: StudentCheckinNotifyTask) {
  if (!task) {
    return
  }
  const taskList = [...signInNotifyState.activeTaskList]
  const nextTaskIdentity = buildTaskIdentity(task)
  const existedIndex = taskList.findIndex(item => buildTaskIdentity(item) === nextTaskIdentity)
  if (existedIndex === -1) {
    taskList.push(task)
  } else {
    taskList.splice(existedIndex, 1, task)
  }
  openSignInNotifyModal({
    activeTaskList: normalizeActiveTaskList(taskList),
    abnormalTaskList: signInNotifyState.abnormalTaskList
  })
}

function handleGlobalSignInNotification(message: GlobalWsMessage) {
  const payload = message.payload || message.data
  if (message.channel === 'WAIT_SIGN_IN_LIST' || message.topic === 'WAIT_SIGN_IN_LIST') {
    fetchAndOpenSignInNotifyModal()
    return
  }
  if (message.type !== 'NOTIFICATION' || !payload) {
    return
  }
  if (!payload.classId || !payload.courseId || !payload.checkInType) {
    return
  }
  openSignInNotifyModalByTask(payload as StudentCheckinNotifyTask)
}

async function fetchAndOpenSignInNotifyModal(params?: { classId?: number }) {
  try {
    const { success, content, message } = await fetchStudentCheckinNotify({
      classId: params?.classId,
    })
    if (success) {
      const normalizedContent = {
        activeTaskList: normalizeActiveTaskList(content?.activeTaskList),
        abnormalTaskList: content?.abnormalTaskList || []
      }
      openSignInNotifyModal(normalizedContent)
      if (normalizedContent.activeTaskList.length === 0 && normalizedContent.abnormalTaskList.length > 0) {
        openAbnormalSignInReminderModal()
      } else {
        clearAbnormalReminderTimer()
      }
      return { success, content: normalizedContent, message }
    } else {
      clearAbnormalReminderTimer()
      Message.error(message)
    }
    return { success, content, message }
  } catch (error: any) {
    clearAbnormalReminderTimer()
    console.error(error)
    Message.error(error?.message || '获取签到提醒失败')
    return {
      success: false,
      content: {
        activeTaskList: [],
        abnormalTaskList: []
      },
      message: error?.message || '获取签到提醒失败'
    }
  }
}

watch(() => router.currentRoute.value.name, () => {
  if (isBlockedSignInPopupRoute()) {
    closeSignInNotifyModal()
    closeAbnormalSignInReminderModal()
  }
}, { immediate: true })

export function useSignInNotifyModal() {
  return {
    signInNotifyState: readonly(signInNotifyState),
    openSignInNotifyModal,
    openSignInNotifyModalByTask,
    closeSignInNotifyModal,
    goToNextSignInNotifyTask,
    goToPrevSignInNotifyTask,
    setCurrentSignInNotifyTask,
    fetchAndOpenSignInNotifyModal,
    getSignInNotifyTaskList,
    handleGlobalSignInNotification
  }
}
